import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * AuthCallback handles the redirect after Google SSO.
 * 
 * Flow:
 * 1. Loading (while fetching profile)
 * 2. Greeting: "Hi, <First Name>! Ready to stand out?"
 * 3. Brand: "RezFlow" in huge text
 * 4. Exit: Fades out
 * 5. Redirect to Dashboard
 */
const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleAuthCallback } = useAuth();
  
  const [phase, setPhase] = useState('loading'); // 'loading', 'greeting', 'brand', 'exit'
  const [firstName, setFirstName] = useState('');
  const [errorStatus, setErrorStatus] = useState(null);

  useEffect(() => {
    const processCallback = async () => {
      const token = searchParams.get('token');
      const isNewUser = searchParams.get('new_user') === 'true';

      if (!token) {
        setErrorStatus('Authentication failed. No token received.');
        setTimeout(() => navigate('/login'), 2500);
        return;
      }

      try {
        const userData = await handleAuthCallback(token);
        sessionStorage.setItem('rzHasLoggedIn', '1'); // mark: known user this session
        if (isNewUser) {
          sessionStorage.setItem('rzShowWelcomeCreditMessage', '1'); // mark: display welcome credit message
        }
        setFirstName(userData.firstName || userData.first_name || 'there');
        
        setPhase('greeting');
        
        // Sequence timings
        setTimeout(() => setPhase('brand'), 2000);
        setTimeout(() => setPhase('exit'), 3500);
        setTimeout(() => navigate('/dashboard'), 4000);
        
      } catch (error) {
        console.error('Auth callback error:', error);
        setErrorStatus('Authentication failed. Please try again.');
        setTimeout(() => navigate('/login'), 2500);
      }
    };

    processCallback();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        
        {errorStatus && (
          <div className="auth-anim-slide-up">
            <div style={styles.errorIcon}>❌</div>
            <p style={styles.errorText}>{errorStatus}</p>
          </div>
        )}

        {!errorStatus && phase === 'loading' && (
          <div style={styles.brandIcon}>⬡</div>
        )}

        {!errorStatus && phase === 'greeting' && (
          <h1 key="greeting" className="auth-anim-slide-up" style={styles.greetingText}>
            Hi, {firstName}! <br />
            <span style={styles.greetingSub}>Ready to stand out?</span>
          </h1>
        )}

        {!errorStatus && (phase === 'brand' || phase === 'exit') && (
          <div className={phase === 'exit' ? 'auth-anim-fade-out' : 'auth-anim-pop-in'} style={styles.brandContainer}>
            <h1 style={styles.hugeBrandTitle}>RezFlow</h1>
          </div>
        )}
        
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)',
    fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  container: {
    textAlign: 'center',
    padding: '40px',
    maxWidth: '600px',
    width: '90%',
  },
  brandIcon: {
    fontSize: '64px',
    color: 'var(--color-accent-ferrari)',
    lineHeight: 1,
    margin: '0 auto',
    width: '80px',
    height: '80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'pulse-glowing 2s infinite ease-in-out',
  },
  errorIcon: {
    fontSize: '64px',
    color: 'var(--color-error)',
    lineHeight: 1,
    margin: '0 auto 24px auto',
    width: '80px',
    height: '80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorText: {
    fontSize: 'var(--text-base)',
    fontWeight: 600,
    color: 'var(--color-error)',
    margin: 0,
  },
  greetingText: {
    fontSize: '48px',
    fontWeight: 800,
    color: 'var(--color-text-primary)',
    margin: '0',
    lineHeight: 1.2,
    letterSpacing: '-1.5px',
  },
  greetingSub: {
    color: 'var(--color-accent-ferrari)',
    fontSize: '40px',
    letterSpacing: '-1px',
  },
  brandContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  hugeBrandTitle: {
    fontSize: '84px',
    fontWeight: 900,
    color: 'var(--color-text-primary)',
    margin: 0,
    letterSpacing: '-3px',
  },
};

// Dynamically inject CSS for the animations
const styleSheet = document.styleSheets[0];
if (styleSheet) {
  try {
    const rules = [
      `@keyframes pulse-glowing {
        0% { transform: scale(1); filter: drop-shadow(0 0 8px rgba(79, 70, 229, 0.25)); }
        50% { transform: scale(1.12); filter: drop-shadow(0 0 24px rgba(79, 70, 229, 0.6)); }
        100% { transform: scale(1); filter: drop-shadow(0 0 8px rgba(79, 70, 229, 0.25)); }
      }`,
      `@keyframes auth-slide-up {
        0% { opacity: 0; transform: translateY(16px); }
        100% { opacity: 1; transform: translateY(0); }
      }`,
      `@keyframes auth-pop-in {
        0% { opacity: 0; transform: scale(0.9); }
        50% { opacity: 1; transform: scale(1.05); }
        100% { opacity: 1; transform: scale(1); }
      }`,
      `@keyframes auth-fade-out {
        0% { opacity: 1; transform: scale(1); filter: blur(0px); }
        100% { opacity: 0; transform: scale(0.95); filter: blur(4px); }
      }`,
      `.auth-anim-slide-up { animation: auth-slide-up 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }`,
      `.auth-anim-pop-in { animation: auth-pop-in 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }`,
      `.auth-anim-fade-out { animation: auth-fade-out 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }`
    ];

    rules.forEach(rule => {
      styleSheet.insertRule(rule, styleSheet.cssRules.length);
    });
  } catch (e) {
    // Graceful fallback
  }
}

export default AuthCallback;
